import { ArrowDropDown } from '@mui/icons-material'
import {
  ClickAwayListener,
  Grow,
  MenuItem,
  MenuList,
  Paper,
  Popper,
} from '@mui/material'
import DuplicateResourceModal from 'components/ResourceList/DuplicateResourceModal'
import { Routes } from 'constants/index'
import { Button } from 'eezy-components'
import { captureError } from 'helpers/error'
import { observer } from 'mobx-react'
import { useSnackbar } from 'notistack'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'
import { useInstance } from 'util/di'

import {
  usePatchApiV1AdminResourcesAssignContributorReviewBatch,
  usePatchApiV1AdminResourcesAssignReviewBatch,
} from 'api'
import {
  AuthStore,
  FlaggedReviewStore,
  GeneralReviewStore,
  ResourceStore,
  SubmittedReviewStore,
} from 'stores'
import { ReviewedResource } from 'types'
import { Resource, ResponsesSingleItemCount } from 'types/api'

import * as S from './styled'

type Props = {
  canSubmit?: boolean
}

const AutoAssignButton = ({ canSubmit = true }: Props): JSX.Element => {
  const { t } = useTranslation()
  const location = useLocation()
  const anchorRef = useRef(null)

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitOptionOpen, setSubmitOptionOpen] = useState(false)
  const [duplicateModalVisibility, setDuplicateModalVisibility] =
    useState<boolean>(false)
  const [submitPath, setSubmitPath] = useState<string>('')
  const navigate = useNavigate()
  const { enqueueSnackbar } = useSnackbar()
  const submittedReviewStore = useInstance('SubmittedReviewStore')
  const flaggedReviewStore = useInstance('FlaggedReviewStore')

  const { visibleResources, setVisible } =
    useInstance<ResourceStore>('ResourceStore')
  const {
    setFilesReviewedCount,
    setCurrentReviewedContributor,
    currentReviewedContributor,
    setReviewedContributorLoading,
    reviewedContributorLoading,
    startNewReviewSession,
    endReviewSession,
    getLatestReviewSession,
    loadingReviewSession,
  } = useInstance<GeneralReviewStore>('GeneralReviewStore')
  const { session } = useInstance<AuthStore>('AuthStore')

  useEffect(() => {
    if (session) {
      getLatestReviewSession()
    }
  }, [getLatestReviewSession, session])

  const stateToAssign = useMemo(() => {
    if (location.pathname.includes('submitted')) {
      return 'submitted'
    } else if (location.pathname.includes('flagged')) {
      return 'flagged'
    }
    return null
  }, [location.pathname])

  const reviewState = useMemo(() => {
    if (/\/submitted\/[^/]+$/.test(location.pathname)) {
      return 'submitted'
    } else if (/\/flagged\/[^/]+$/.test(location.pathname)) {
      return 'flagged'
    }
    return null
  }, [location.pathname])

  const { mutate: assignNewBatch } =
    usePatchApiV1AdminResourcesAssignReviewBatch({
      queryParams: {
        contributor_import_guid: currentReviewedContributor || '',
        reviewer_import_guid: session?.id || '',
        state: stateToAssign ?? 'submitted',
      },
    })

  const { mutate: assignBatch, loading: batchLoading } =
    usePatchApiV1AdminResourcesAssignContributorReviewBatch({
      queryParams: {
        state: stateToAssign ?? 'submitted',
      },
    })

  const store: SubmittedReviewStore | FlaggedReviewStore = useMemo(() => {
    if (stateToAssign === 'submitted') {
      return submittedReviewStore as SubmittedReviewStore
    } else if (stateToAssign === 'flagged') {
      return flaggedReviewStore as FlaggedReviewStore
    } else {
      return submittedReviewStore as SubmittedReviewStore
    }
  }, [stateToAssign, submittedReviewStore, flaggedReviewStore])

  const selectedVisibleIds = store.selectedVisible.map(({ id }) => id)
  const submittableResources = store.reviewedResources.filter(reviewed =>
    selectedVisibleIds.includes(reviewed.resource.id)
  )
  const submittableCount = submittableResources.length
  const hasDuplicates = submittableResources.some(r => r.resource.duplicate_id)
  const donePath = store.state.includes('submitted')
    ? Routes.Submitted
    : Routes.Flagged

  const assignNewBatchWithRetries = useCallback(
    async (maxRetries = 5, delay = 250) => {
      let attempts = 0

      while (attempts < maxRetries) {
        // @ts-ignore
        const { count }: ResponsesSingleItemCount = await assignNewBatch()
        if (count > 0) {
          return { count }
        } else {
          attempts += 1
          if (attempts < maxRetries) {
            await new Promise(resolve => setTimeout(resolve, delay))
          }
        }
      }
      return { count: 0 }
    },
    [assignNewBatch]
  )

  const assignBatchWithRetries = useCallback(
    async (maxRetries = 5, delay = 250) => {
      let attempts = 0
      const contributorIds: string[] = []

      while (attempts < maxRetries) {
        // @ts-ignore
        const { count, item_type }: ResponsesSingleItemCount =
          // @ts-ignore
          await assignBatch({
            last_contributor_ids:
              contributorIds.length > 0 ? contributorIds : undefined,
          })

        if (item_type) {
          contributorIds.push(item_type)
        }

        if (count > 0 && contributorIds.length > 0) {
          return { count, item_type: contributorIds[contributorIds.length - 1] }
        } else {
          attempts += 1
          if (attempts < maxRetries) {
            await new Promise(resolve => setTimeout(resolve, delay))
          }
        }
      }
      return { count: 0, item_type: null }
    },
    [assignBatch]
  )

  const assignBatchWithContributorId = useCallback(
    async (delay = 250, timeLimitMs = 120000) => {
      const startTime = Date.now()
      const contributorIds: string[] = []

      // eslint-disable-next-line no-constant-condition
      while (true) {
        // Check if we've exceeded the time limit (2 minutes by default)
        if (Date.now() - startTime > timeLimitMs) {
          throw new Error(
            'Failed to retrieve a contributor within the time limit.'
          )
        }

        // @ts-ignore
        const { count, item_type }: ResponsesSingleItemCount =
          // @ts-ignore
          await assignBatch({
            last_contributor_ids:
              contributorIds.length > 0 ? contributorIds : undefined,
          })

        if (item_type) {
          contributorIds.push(item_type)
        }

        if (count > 0 && contributorIds.length > 0) {
          return { count, item_type: contributorIds[contributorIds.length - 1] }
        }

        await new Promise(resolve => setTimeout(resolve, delay))
      }
    },
    [assignBatch]
  )

  const handleSubmitError = (
    error: unknown,
    shouldShowGenericError = false
  ) => {
    const message = `Bulk Actions error while submitting reviewed resources - ${
      submitPath ?? 'missing'
    } path - ResourceList/Actions/Bulk.tsx`

    setIsSubmitting(false)
    setSubmitPath('')
    setDuplicateModalVisibility(false)

    enqueueSnackbar(
      t(
        shouldShowGenericError
          ? 'errors.requests.generic'
          : 'review.review_no_contrib'
      ),
      {
        variant: 'error',
      }
    )
    captureError(error, message)
  }

  const submitResources = async (
    reviewedResources: ReviewedResource[]
  ): Promise<Resource[]> => {
    setIsSubmitting(true)
    const { succeededIds } = await store.submitReviewedResources(
      '',
      reviewedResources
    )

    const successfulIds = succeededIds.map(String)

    store.setReviewedResources(
      store.reviewedResources.filter(
        ({ resource }) => !successfulIds.includes(String(resource.id))
      )
    )

    const remainingVisible = visibleResources
      .filter(res => !successfulIds.includes(String(res.id)))
      .map(res => res)

    const updatedCount = store.totalPendingCount - successfulIds.length
    const updatedFilteredCount = store.totalFilteredCount - successfulIds.length

    store.setTotalPendingCount(updatedCount)
    store.setTotalFilteredCount(updatedFilteredCount)
    setIsSubmitting(false)
    setSubmitPath('')
    setDuplicateModalVisibility(false)
    return remainingVisible
  }

  const submitResourcesAndContinue = async (
    reviewedResources: ReviewedResource[],
    bypass?: boolean
  ) => {
    setSubmitPath('continue')

    if (!bypass && hasDuplicates) {
      setDuplicateModalVisibility(true)
    } else {
      try {
        const remainingVisible = await submitResources(reviewedResources)

        setFilesReviewedCount(reviewedResources.length)

        if (remainingVisible.length > 0) {
          setVisible(remainingVisible)
        } else {
          setReviewedContributorLoading(true)
          const { count } = await assignNewBatchWithRetries()
          const contributorId = currentReviewedContributor

          if (count > 0) {
            window.location.href = `${donePath}/${contributorId}`
          } else {
            const result = await assignBatchWithContributorId()
            if (result.count > 0 && result.item_type) {
              setCurrentReviewedContributor(result.item_type)
              navigate(`${donePath}/${result.item_type}`)
            } else {
              await endReviewSession()
              navigate(donePath)
            }
          }
        }
      } catch (error) {
        await endReviewSession()
        navigate(donePath)
        handleSubmitError(error)
      } finally {
        setReviewedContributorLoading(false)
      }
    }
  }

  const submitResourcesAndExit = async (
    reviewedResources: ReviewedResource[],
    bypass?: boolean
  ) => {
    setSubmitPath('exit')

    if (!bypass && hasDuplicates) {
      setDuplicateModalVisibility(true)
    } else {
      try {
        await submitResources(reviewedResources)
        setFilesReviewedCount(reviewedResources.length)
        await store.unassignResources()
        await endReviewSession()
        navigate(donePath)
      } catch (error) {
        await endReviewSession()
        navigate(donePath)
        handleSubmitError(error, true)
      } finally {
        setCurrentReviewedContributor(undefined)
      }
    }
  }

  const handleSubmit = () => {
    if (submitPath === 'continue') {
      submitResourcesAndContinue(submittableResources, true)
    } else if (submitPath === 'exit') {
      submitResourcesAndExit(submittableResources, true)
    } else {
      return
    }
  }

  const handleStartReview = useCallback(async () => {
    try {
      setReviewedContributorLoading(true)
      const { count, item_type } = await assignBatchWithRetries()
      const contributorId = item_type

      if (count > 0 && contributorId) {
        await startNewReviewSession()
        setCurrentReviewedContributor(contributorId)
        navigate(`${donePath}/${contributorId}`)
      } else {
        const result = await assignBatchWithContributorId()
        if (result.count > 0 && result.item_type) {
          await startNewReviewSession()
          setCurrentReviewedContributor(result.item_type)
          navigate(`${donePath}/${result.item_type}`)
        } else {
          enqueueSnackbar(t('review.review_no_contrib'), {
            variant: 'error',
          })
        }
      }
    } catch (error) {
      enqueueSnackbar(t('util.something_went_wrong'), {
        variant: 'error',
      })
      captureError(
        error,
        'Error while starting review - AutoAssignButton/index.tsx'
      )
    } finally {
      setReviewedContributorLoading(false)
    }
  }, [
    assignBatchWithContributorId,
    assignBatchWithRetries,
    donePath,
    enqueueSnackbar,
    navigate,
    setCurrentReviewedContributor,
    setReviewedContributorLoading,
    startNewReviewSession,
    t,
  ])

  if (session?.canReviewContributors.isLegacyReviewer) {
    return <></>
  }

  if (canSubmit && (reviewState === 'flagged' || reviewState === 'submitted')) {
    return (
      <>
        <S.StyledGroup
          variant="contained"
          color="primary"
          aria-label="submission button group"
          ref={anchorRef}
        >
          <S.SubmitButton
            color="primary"
            disabled={
              !submittableCount ||
              isSubmitting ||
              batchLoading ||
              reviewedContributorLoading ||
              loadingReviewSession
            }
            loading={
              isSubmitting ||
              batchLoading ||
              reviewedContributorLoading ||
              loadingReviewSession
            }
            onClick={() => submitResourcesAndContinue(submittableResources)}
            testId="submit-batch"
          >
            {t('review.pending_submissions.submit')}
            &nbsp;
            {submittableCount && submittableCount > 1
              ? `(${submittableCount})`
              : ''}
          </S.SubmitButton>
          <S.DropdownButton
            disabled={!submittableCount || isSubmitting}
            color="primary"
            onClick={() => setSubmitOptionOpen(true)}
            testId="dropdown-button"
          >
            <ArrowDropDown />
          </S.DropdownButton>
        </S.StyledGroup>
        <Popper
          open={submitOptionOpen}
          anchorEl={anchorRef.current}
          placement="bottom-end"
          role={undefined}
          transition
          disablePortal
        >
          {({ TransitionProps, placement }) => (
            <Grow
              {...TransitionProps}
              style={{
                transformOrigin:
                  placement === 'bottom' ? 'right top' : 'right bottom',
              }}
            >
              <Paper>
                <ClickAwayListener
                  onClickAway={() => setSubmitOptionOpen(false)}
                >
                  <MenuList id="split-button-menu">
                    <MenuItem
                      data-testid="submit-and-exit"
                      onClick={() =>
                        submitResourcesAndExit(submittableResources)
                      }
                    >
                      {t('review.pending_submissions.submit_exit')}
                    </MenuItem>
                  </MenuList>
                </ClickAwayListener>
              </Paper>
            </Grow>
          )}
        </Popper>
        <DuplicateResourceModal
          isOpen={duplicateModalVisibility}
          setOpen={() => setDuplicateModalVisibility(!duplicateModalVisibility)}
          submitResources={handleSubmit}
          loading={isSubmitting}
        />
      </>
    )
  }

  return (
    <Button
      color="primary"
      onClick={handleStartReview}
      loading={
        batchLoading || reviewedContributorLoading || loadingReviewSession
      }
      disabled={
        batchLoading || reviewedContributorLoading || loadingReviewSession
      }
    >
      {t('review.start_review')}
    </Button>
  )
}

export default observer(AutoAssignButton)
